import { supabase } from '../lib/supabaseClient';

export interface AutoSourcedVybe {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'published';
  source?: string;
  source_url?: string;
  source_data_hash?: string;
  title: string;
  description: string;
  event_date: string;
  venue_name?: string;
  area_label: string;
  postcode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  category: string;
  is_free: boolean;
  raw?: any;
  published_vybe_id?: string;
  published_at?: string;
  published_by?: string;
  admin_notes?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface GetAutoSourcedVybesResult {
  data: AutoSourcedVybe[];
  count: number | null;
}

export interface GetAutoSourcedVybesFilters {
  status?: 'pending' | 'approved' | 'rejected' | 'published';
  source?: string;
  limit?: number;
  offset?: number;
}

export interface PublishResult {
  success: boolean;
  published: number;
  skipped: number;
  errors: string[];
  validationErrors: Array<{
    id: string;
    title: string;
    errors: string[];
  }>;
}

export async function getAutoSourcedVybes(
  filters?: GetAutoSourcedVybesFilters
): Promise<GetAutoSourcedVybesResult> {
  let query = supabase
    .from('auto_sourced_vybes')
    .select('*', { count: 'exact' });

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.source) {
    query = query.eq('source', filters.source);
  }

  query = query.order('created_at', { ascending: false });

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  if (filters?.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching auto sourced vybes:', error);
    throw error;
  }

  return {
    data: data || [],
    count,
  };
}

export async function approveVybe(id: string): Promise<void> {
  const { error } = await supabase
    .from('auto_sourced_vybes')
    .update({ status: 'approved' })
    .eq('id', id);

  if (error) {
    console.error('Error approving vybe:', error);
    throw error;
  }
}

export async function rejectVybe(id: string, reason?: string): Promise<void> {
  const { error } = await supabase
    .from('auto_sourced_vybes')
    .update({
      status: 'rejected',
      rejection_reason: reason ?? null,
    })
    .eq('id', id);

  if (error) {
    console.error('Error rejecting vybe:', error);
    throw error;
  }
}

export async function updateVybe(id: string, data: Partial<AutoSourcedVybe>): Promise<void> {
  const { error } = await supabase
    .from('auto_sourced_vybes')
    .update(data)
    .eq('id', id);

  if (error) {
    console.error('Error updating vybe:', error);
    throw error;
  }
}

export async function publishApprovedVybes(): Promise<PublishResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/publish-auto-sourced-vybes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to publish vybes: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  return result;
}

export async function insertTestSeed(): Promise<{ success: boolean; id: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-insert-test-seed`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to insert test seed: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  return result;
}

export interface EnrichLocationResult {
  success: boolean;
  message?: string;
  skipped?: boolean;
  enriched?: {
    venue_name: string;
    latitude: number;
    longitude: number;
    postcode: string;
    area_label: string;
    formatted_address: string;
  };
  error?: string;
  query_used?: string;
}

export async function enrichLocation(id: string): Promise<EnrichLocationResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/enrich-seeded-vybe-location`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `Failed to enrich location: ${response.status}`);
  }

  const result = await response.json();
  return result;
}

export interface RunSeederResult {
  success: boolean;
  daysAhead: number;
  dateFrom: string;
  dateTo: string;
  fetched: number;
  inserted: number;
  skipped_out_of_range: number;
  skipped_duplicate: number;
  skipped_validation: number;
  skipped_major_event: number;
  errors: any[];
  validationErrors: any[];
}

export async function runSeederNow(daysAhead?: number): Promise<RunSeederResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('Please log in again');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-run-seeder-kent`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ daysAhead: daysAhead || 30 }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to run seeder: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  return result;
}

export async function runMeetupSeederNow(daysAhead?: number): Promise<RunSeederResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('Please log in again');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-run-seeder-meetup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ daysAhead: daysAhead || 30 }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to run Meetup seeder: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  return result;
}

export async function runGetYourGuideSeederNow(daysAhead?: number): Promise<RunSeederResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;

  if (!session?.access_token) {
    throw new Error('Please log in again');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing VITE_SUPABASE_URL');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-run-seeder-getyourguide`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ daysAhead: daysAhead || 30 }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to run GetYourGuide seeder: ${response.status} ${errorText}`);
  }

  const result = await response.json();
  return result;
}
